import { Icon } from "icons";
import { cn } from "ui";

import { type PublicProfilePageLink } from "src/pages/PublicProfilePage";
import {
  getSafeProfileHref,
  getSocialIconName,
  getSocialProfileUrl,
} from "src/profile/socialPlatforms";

function getSocialUrl(link: PublicProfilePageLink): string {
  const href = link.resource.url ?? link.resource.fileUrl;

  if (!href) {
    return "#";
  }

  if (link.platform === "email") {
    return (
      getSafeProfileHref(
        href.startsWith("mailto:") ? href : `mailto:${href}`,
      ) ?? "#"
    );
  }

  if (link.platform === "phone") {
    return (
      getSafeProfileHref(href.startsWith("tel:") ? href : `tel:${href}`) ?? "#"
    );
  }

  const socialUrl = getSocialProfileUrl(link.platform, href);

  if (socialUrl) {
    return getSafeProfileHref(socialUrl) ?? "#";
  }

  return getSafeProfileHref(href) ?? "#";
}

function getLinkTitle(link: PublicProfilePageLink): string {
  if (link.titleOverride) {
    return link.titleOverride;
  }

  return (
    link.resource.title ?? link.resource.fileName ?? link.resource.url ?? ""
  );
}

interface PublicProfileSocialLinksProps {
  links: PublicProfilePageLink[];
  color: string;
  className: string;
}

export function PublicProfileSocialLinks(props: PublicProfileSocialLinksProps) {
  if (props.links.length === 0) {
    return null;
  }

  return (
    <div className={cn("flex justify-center gap-4", props.className)}>
      {props.links.map((link) => {
        return (
          <a
            key={link.id}
            href={getSocialUrl(link)}
            target={
              link.platform === "email" || link.platform === "phone"
                ? undefined
                : "_blank"
            }
            rel="ugc noopener noreferrer"
            title={getLinkTitle(link)}
            className="inline-flex items-center justify-center"
            style={{ color: props.color }}
          >
            <Icon name={getSocialIconName(link.platform)} className="size-5" />
          </a>
        );
      })}
    </div>
  );
}
